'use client';
import {
  cvaArrowIcon,
  cvaPagination,
  cvaPaginationBreak,
  cvaPaginationLink,
  cvaPaginationLinkActive,
  cvaPaginationPage,
} from '@/widgets/Pagination/ui/PaginationStyles';
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { FC, useMemo } from 'react';
import { useMedia } from 'react-use';
import ArrowRightIcon from '~/icons/arrow-right.svg';
import { PREFETCH } from '@/shared/constants';

interface Props {
  pages: number;
  currentPage: number;
  hrefBuilder: (page: number) => string;
  onPageClick: (page: number) => void;
}

const Pagination: FC<Props> = ({ pages, currentPage, hrefBuilder, onPageClick }) => {
  const isMobile = useMedia('(max-width: 767px)');
  const siblingCount = isMobile ? 0 : 1;

  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
  };

  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingCount + 5;
    const totalPages = pages;

    if (totalPageNumbers >= pages) {
      return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 2 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, 'DOTS', totalPages];
    }

    /*
    	Case 3: No right dots to show, but left dots to be shown
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 2 + 2 * siblingCount;
      const rightRange = range(pages - rightItemCount + 1, pages);
      return [firstPageIndex, 'DOTS', ...rightRange];
    }

    /*
    	Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, 'DOTS', ...middleRange, 'DOTS', lastPageIndex];
    }
  }, [pages, siblingCount, currentPage]);

  return pages > 1 ? (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.25,
        ease: 'easeInOut',
      }}
    >
      <div className={cvaPagination()}>
        <Link
          href={hrefBuilder(currentPage - 1)}
          onClick={() => onPageClick(currentPage - 1)}
          className={cx(
            cvaPaginationPage({
              bg: true,
              disabled: currentPage === 1,
            })
          )}
          prefetch={PREFETCH.PAGINATION}
        >
          <span className={cvaPaginationLink()}>
            <ArrowRightIcon
              className={cvaArrowIcon({
                direction: 'prev',
              })}
            />
          </span>
        </Link>

        {paginationRange?.length
          ? paginationRange.map((i, index) =>
              i === 'DOTS' ? (
                <div className={cvaPaginationBreak()} key={index}>
                  <span className={cvaPaginationLink()}>...</span>
                </div>
              ) : (
                <Link
                  key={index}
                  href={hrefBuilder(parseInt(`${i}`))}
                  onClick={() => onPageClick(parseInt(`${i}`))}
                  className={cvaPaginationPage()}
                  prefetch={PREFETCH.PAGINATION}
                >
                  <span className={cx(cvaPaginationLink(), currentPage === i && cvaPaginationLinkActive())}>{i}</span>
                </Link>
              )
            )
          : null}

        <Link
          href={hrefBuilder(currentPage + 1)}
          onClick={() => onPageClick(currentPage + 1)}
          className={cx(
            cvaPaginationPage({
              bg: true,
              disabled: currentPage === pages,
            })
          )}
          prefetch={PREFETCH.PAGINATION}
        >
          <span className={cvaPaginationLink()}>
            <ArrowRightIcon
              className={cvaArrowIcon({
                direction: 'next',
              })}
            />
          </span>
        </Link>
      </div>
    </motion.div>
  ) : null;
};

export default Pagination;
